import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import * as customPropTypes from '@/custom-prop-types';
import { Button } from '@/design-system/atoms/Button';
import { Image } from '@/design-system/atoms/Image';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';

import styles from './Banner5050.module.scss';

const Banner5050 = ({
    testId,
    theme = Theme.NAME.LOCATIONS,
    title,
    body,
    text,
    cta,
    media,
    illustration,
    illustrationShape,
    image,
    ...props
}) => {
    const hasMedia = (media === 'illustration' && illustration) || (media === 'image' && image);

    return (
        <div
            data-testid={testId}
            className={classnames(
                styles['banner-5050'],
                hasMedia
                    ? illustration
                        ? illustrationShape
                            ? styles[`banner-5050--illustration-${illustrationShape}`]
                            : styles['banner-5050--illustration-free-floating']
                        : image
                          ? styles['banner-5050--image']
                          : undefined
                    : undefined,
            )}
            {...props}
        >
            <Theme
                name={theme}
                className={classnames(
                    styles['banner-5050__main'],
                    styles[`banner-5050__main--rounded`],
                )}
            >
                <div className={styles['banner-5050__wrapper']}>
                    <div className={styles['banner-5050__primary']}>
                        <div className={styles['banner-5050__content']}>
                            <div className={styles['banner-5050__title']}>
                                <Title
                                    tag="h2"
                                    variant={Title.VARIANT.H4}
                                    content={title}
                                    lineClamp={{ sm: 3, lg: 2 }}
                                />
                            </div>
                            {body && (
                                <div className={styles['banner-5050__body']}>
                                    <Text
                                        variant={Text.VARIANT.T2}
                                        content={body}
                                        lineClamp={{ sm: 3, lg: 2 }}
                                    />
                                </div>
                            )}
                        </div>
                        {cta && cta?.href && (
                            <div className={styles['banner-5050__button-wrapper']}>
                                <Button
                                    {...cta}
                                    className={styles['banner-5050__button']}
                                    href={cta.href}
                                    target={cta.target}
                                    csr={cta.csr}
                                    buttonStyle={cta.buttonStyle}
                                    label={cta.label}
                                    ariaLabel={cta.ariaLabel}
                                    analytics={{
                                        componentName: '5050 Banner',
                                        componentTitle: title,
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    {hasMedia && (
                        <div className={styles['banner-5050__secondary']}>
                            <div className={styles['banner-5050__media']}>
                                {media === 'illustration' && illustration && (
                                    <>
                                        {/* Illustration FPO */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 162 162"
                                        >
                                            <rect width="162" height="162" fill="#cccccc"></rect>
                                            <text x="50%" y="50%" textAnchor="middle">
                                                162x162
                                            </text>
                                        </svg>
                                    </>
                                )}

                                {media === 'image' && image && (
                                    <Image
                                        {...image}
                                        mobileAspectRatio={Image.ASPECT_RATIO['1:1']}
                                        defaultAspectRatio={Image.ASPECT_RATIO['1:1']}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Theme>
        </div>
    );
};

Banner5050.propTypes = {
    /** Test ID */
    testId: PropTypes.string,
    /** Theme */
    theme: PropTypes.oneOf(Object.values(Theme.NAME)),
    /** Title */
    title: PropTypes.string.isRequired,
    /** Body Text */
    body: PropTypes.string,
    /** Link */
    cta: customPropTypes.linkPropType,
    /** Media type to display */
    media: PropTypes.oneOf(['image', 'illustration']),
    /** Illustration */
    illustration: PropTypes.object,
    /** Illustration Type */
    illustrationShape: PropTypes.oneOf(['free-floating', 'circle']),
    /** Image */
    image: PropTypes.shape(Image.propTypes),
};

export default Banner5050;
